import {useLazyQuery, useQuery} from "@apollo/client";
import union from "lodash/union";
import pluralize from "pluralize";
import {useCallback, useEffect, useMemo, useState} from "react";
import {FiAlertCircle} from "react-icons/fi";
import {useDebounce} from "use-debounce";

import {Button} from "@/components/button/Button.tsx";
import {CheckTypeSelect} from "@/components/checkTypeSelect/CheckTypeSelect.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {MatchMode} from "@/components/filterDropdown/FilterDropdown.ts";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {ListPanelSelect, ListPanelSelectOption} from "@/components/listPanelSelect/ListPanelSelect.tsx";
import {MatchModeToggle} from "@/components/matchModeToggle/MatchModeToggle.tsx";
import {LinkedSchedulesChip} from "@/components/multiServiceSelect/LinkedSchedulesChip.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {TagSelect} from "@/components/TagSelect/TagSelect.tsx";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {GetServiceTypesAndTagsResult} from "@/lib/queries/checks.ts";
import {
  GQL_MAINTENANCE_SCHEDULE_SERVICE_COUNTS,
  MaintenanceScheduleServiceCountsResponse,
} from "@/lib/queries/maintenance.ts";
import {FindChecksResult, GQL_FIND_CHECKS} from "@/lib/queries/statuspages.ts";
import {MonitoringServiceType, ServiceTag, ServiceType} from "@/lib/types/checks.ts";

import styles from "./MultiServiceSelect.module.scss";

export interface LinkedScheduleConfig {
  showLinkedSchedules: boolean;
  currentScheduleId?: number;
}

export interface MultiServiceSelectProps extends GetServiceTypesAndTagsResult {
  skipRequest?: boolean;
  className?: string;
  defaultValue?: number[];
  excludeIds?: number[];
  excludeMonitoringServiceType?: MonitoringServiceType;
  onAppend: (value: number) => void;
  onReplace: (values: number[]) => void;
  scheduleConfig?: LinkedScheduleConfig;
  /** Max checks to fetch into the selector. Defaults to SERVICE_LIMIT; pass `null` for no limit (fetch all matches). */
  serviceLimit?: number | null;
}

const SERVICE_LIMIT = 200;

// Options render into an always-open react-select with no virtualization, so cap how many rows
// reach the DOM. Selection operates on the full id arrays, so "Select All" and the saved value
// are unaffected — only what is visible is limited. Users narrow the rest via search/type/tags.
const RENDERED_OPTIONS_LIMIT = 200;

// `serviceLimit={null}` means "no limit". Until findChecks supports an unbounded fetch, map it to a
// large ceiling that covers realistic accounts; the DOM stays bounded by RENDERED_OPTIONS_LIMIT.
const NO_LIMIT_FETCH_SIZE = 10_000;

/*
form context-less SelectServiceBox aimed for reusability.
Replace - replaces all selected checks with new ones,
Append - adds new checks to the existing ones.
 */
export const MultiServiceSelect = ({
  monitoringServiceTypes,
  excludeMonitoringServiceType,
  className,
  tags,
  defaultValue,
  skipRequest,
  excludeIds,
  onAppend,
  onReplace,
  scheduleConfig,
  serviceLimit = SERVICE_LIMIT,
}: MultiServiceSelectProps) => {
  // `searchChecks` fetches display data (force-including the initially-selected checks so the
  // "selected" panel can render them even when they don't match the active filter). `searchMatches`
  // fetches only the ids matching the current filter (no force-include), keyed on the filter alone —
  // so selecting/deselecting never refetches and the available list stays filter-aware.
  const [searchChecks, {data, previousData}] = useLazyQuery<FindChecksResult>(GQL_FIND_CHECKS);
  const [searchMatches, {loading: matchesLoading}] = useLazyQuery<FindChecksResult>(GQL_FIND_CHECKS);
  const [serviceType, setServiceType] = useState<ServiceType | null>();
  const [selectedTags, setSelectedTags] = useState<readonly ServiceTag[]>([]);
  const [tagsMatch, setTagsMatch] = useState<MatchMode>("OR");
  const [search, setSearch] = useState("");
  const [searchValue] = useDebounce(search, 300);
  const [selectedChecks, setSelectedChecks] = useState<number[]>(defaultValue || []);
  const [filterMatchedChecks, setFilterMatchedChecks] = useState<number[]>([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialChecks = useMemo(() => new Set(defaultValue || []), []);

  const tagsVariable = useMemo(() => (selectedTags.length ? selectedTags.map((t) => t.id) : undefined), [selectedTags]);

  useEffect(() => {
    if (skipRequest) {
      return;
    }

    const variables = {
      search,
      serviceType: serviceType?.name,
      excludeServiceType: excludeMonitoringServiceType,
      tags: tagsVariable,
      tagsMatch,
      limit: serviceLimit ?? NO_LIMIT_FETCH_SIZE,
    };
    searchChecks({variables: {...variables, include: defaultValue}});
    searchMatches({variables}).then(({data}) => {
      const matched = (data?.findChecks?.items || []).map((i) => i.id).filter((i) => !(excludeIds || []).includes(i));
      setFilterMatchedChecks(matched);
    });
    // eslint-disable-next-line
  }, [searchValue, serviceType, tagsVariable, tagsMatch, skipRequest, serviceLimit]);

  // Fall back to the previous result while a search refetch is in flight so the panels keep their
  // rows instead of blinking empty. Saved checks are force-included in every fetch, so filtering the
  // stale result by the new search term still surfaces the right selected rows until fresh data lands.
  const checks = (data ?? previousData)?.findChecks?.items || [];

  // Membership set shared by everything that asks "is this check selected?". Both callers below
  // run against the full selection, which reaches NO_LIMIT_FETCH_SIZE when serviceLimit is null,
  // so a linear `includes` per lookup made each add/remove click quadratic in the selection size.
  const selectedChecksSet = useMemo(() => new Set(selectedChecks), [selectedChecks]);

  // Available = checks matching the current filter that aren't already selected. Derived, so
  // add/remove is instant local state and a deselected check only reappears here if it matches.
  const availableChecks = useMemo(
    () => filterMatchedChecks.filter((id) => !selectedChecksSet.has(id)),
    [filterMatchedChecks, selectedChecksSet]
  );

  // One server-side aggregate for the whole selection instead of one query per chip. Debounced so
  // rapid check selection collapses into a single request, and we fall back to the previous result
  // while a refetch is in flight so the existing chips keep their counts instead of blinking out.
  const [debouncedSelectedChecks] = useDebounce(selectedChecks, 300);

  const {data: serviceCountsData, previousData: previousServiceCountsData} =
    useQuery<MaintenanceScheduleServiceCountsResponse>(GQL_MAINTENANCE_SCHEDULE_SERVICE_COUNTS, {
      variables: {serviceIds: debouncedSelectedChecks, excludeScheduleId: scheduleConfig?.currentScheduleId},
      skip: !scheduleConfig?.showLinkedSchedules || debouncedSelectedChecks.length === 0,
    });

  const serviceCounts = serviceCountsData ?? previousServiceCountsData;

  const linkedScheduleCountByCheck = useMemo(() => {
    const counts = new Map<number, number>();
    for (const {service_id, count} of serviceCounts?.counts ?? []) {
      counts.set(service_id, count);
    }
    return counts;
  }, [serviceCounts]);

  const linkedSchedulesAccessory = useCallback(
    (option: ListPanelSelectOption) => (
      <LinkedSchedulesChip
        checkId={option.value}
        checkName={option.title}
        count={linkedScheduleCountByCheck.get(option.value) ?? 0}
        excludeScheduleId={scheduleConfig?.currentScheduleId}
      />
    ),
    [scheduleConfig?.currentScheduleId, linkedScheduleCountByCheck]
  );

  const recentlyRemovedAccessory = useCallback(
    (option: ListPanelSelectOption) => {
      if (!initialChecks.has(option.value) || selectedChecksSet.has(option.value)) return null;
      return (
        <Tooltip tooltipContent="This check was recently removed from this schedule." placement="top">
          <Icon icon={FiAlertCircle} className={"pulse-color-orange-500"} />
        </Tooltip>
      );
    },
    [initialChecks, selectedChecksSet]
  );

  const getListPanelValues = (filterArray: number[], renderLimit?: number, searchTerm?: string) => {
    const filterSet = new Set(filterArray);
    const query = searchTerm?.trim().toLowerCase();
    const values: ListPanelSelectOption[] = [];
    for (const {id, name, address, type} of checks) {
      if (!filterSet.has(id)) continue;
      if (
        query &&
        !name.toLowerCase().includes(query) &&
        !address.toLowerCase().includes(query) &&
        !String(id).includes(query)
      ) {
        continue;
      }
      values.push({value: id, title: name, subtitle: address, tag: type});
      if (renderLimit !== undefined && values.length >= renderLimit) break;
    }
    return values;
  };

  // With a serviceLimit the selection can't exceed it, so render everything. With null the selection
  // is unbounded, so cap the DOM — the search above filters this panel too, so any check hidden by the
  // cap stays reachable: search for it, then click to remove.
  const selectedRenderLimit = serviceLimit === null ? RENDERED_OPTIONS_LIMIT : undefined;
  const selectedMatches = getListPanelValues(selectedChecks, undefined, searchValue);
  const selectedPanelValues =
    selectedRenderLimit !== undefined ? selectedMatches.slice(0, selectedRenderLimit) : selectedMatches;
  // Total the "Showing N of M" hint counts against: the matching subset while searching, else the
  // full selection (the label above always shows the true total, so this can safely track the filter).
  const selectedHintTotal = searchValue.trim() ? selectedMatches.length : selectedChecks.length;

  return (
    <div className={className}>
      <div className="d-flex gap-3 flex-column flex-sm-row">
        <div className={styles.container}>
          <FormLabel
            forId="multi-service-search"
            helpText="Search by name or address to filter both the available and selected Checks"
          >
            Manually Select Checks
          </FormLabel>
          <SearchInput onChange={setSearch} id="multi-service-search" />
        </div>
        <div className={styles.container}>
          <FormLabel forId="multi-service-type">Type</FormLabel>
          <CheckTypeSelect
            id="multi-service-type"
            value={serviceType}
            placeholder="Type"
            onChange={(v) => setServiceType(v)}
            options={monitoringServiceTypes.map(({group, types}) => ({label: group, options: types}))}
            isClearable
          />
        </div>
      </div>
      <div className="mt-3">
        <FormLabel
          forId="multi-service-tags"
          helpText="Filter checks by one or more tags. Matches checks with any of the selected tags."
        >
          Tags
        </FormLabel>
        <TagSelect
          id="multi-service-tags"
          isMulti
          options={tags}
          onChange={(value) => setSelectedTags(value ?? [])}
          value={selectedTags}
          placeholder="Select Tags"
          isClearable
        />
        <div className="mt-3">
          <MatchModeToggle value={tagsMatch} onChange={setTagsMatch} />
        </div>
      </div>
      <Divider className="my-3" />
      <div className="d-flex gap-3 flex-column flex-sm-row">
        <div className={styles.container}>
          <FormLabel forId="checks-available" helpText="Click on Checks to add" className="mb-2">
            Checks Available
          </FormLabel>
          <div className={styles.listContainer}>
            <ListPanelSelect
              id="checks-available"
              className={styles.listPanel}
              noOptionsText={matchesLoading ? <LoadingContainer /> : "No Checks Available"}
              onChange={(items) => {
                const elToAdd = items.find(({value}) => !selectedChecks.includes(value))!.value;
                setSelectedChecks([...selectedChecks, elToAdd]);
                onAppend(elToAdd);
              }}
              options={getListPanelValues(availableChecks, RENDERED_OPTIONS_LIMIT)}
              value={getListPanelValues(selectedChecks)}
              hideTag
              optionAccessory={scheduleConfig?.showLinkedSchedules ? recentlyRemovedAccessory : undefined}
            />
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <Button
              variant="linkBlue"
              size="fitContent"
              typography="linkBold"
              onClick={() => {
                const selected = union(selectedChecks, availableChecks);
                setSelectedChecks(selected);
                onReplace(selected);
              }}
            >
              Select All
            </Button>
            {availableChecks.length > RENDERED_OPTIONS_LIMIT && (
              <div className="pulseTextBodySmall pulse-color-gray-600">
                {`Showing ${RENDERED_OPTIONS_LIMIT} of ${availableChecks.length}`}
              </div>
            )}
          </div>
        </div>
        <div className={styles.container}>
          <FormLabel forId="checks-selected" helpText="Click on Checks to remove" className="mb-2">
            {`${pluralize("Check", selectedChecks.length, true)} Selected`}
          </FormLabel>
          <div className={styles.listContainer}>
            <ListPanelSelect
              id="checks-selected"
              className={styles.listPanel}
              noOptionsText="No Checks Selected"
              onChange={([{value}]) => {
                // Local state only — `availableChecks` re-derives from the filter match set, so the
                // deselected check reappears in the available list only if it matches the filter.
                const newSelectedChecks = selectedChecks.filter((c) => c !== value);
                setSelectedChecks(newSelectedChecks);
                onReplace(newSelectedChecks);
              }}
              options={selectedPanelValues}
              value={[]}
              hideTag
              optionAccessory={scheduleConfig?.showLinkedSchedules ? linkedSchedulesAccessory : undefined}
            />
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <Button
              variant="linkBlue"
              size="fitContent"
              typography="linkBold"
              onClick={() => {
                setSelectedChecks([]);
                onReplace([]);
              }}
            >
              Remove All
            </Button>
            {selectedPanelValues.length < selectedHintTotal && (
              <div className="pulseTextBodySmall pulse-color-gray-600">
                {`Showing ${selectedPanelValues.length} of ${selectedHintTotal}`}
              </div>
            )}
            {serviceLimit !== null && (
              <div className="pulseTextBodySmall pulse-color-gray-700">{`Max. ${serviceLimit} Checks`}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
